import { useState, useEffect, useCallback } from 'react';
import { FotoboxRecord } from '../types';

const API_URL = import.meta.env.VITE_API_URL || '';

function sortBoxes(boxes: FotoboxRecord[]): FotoboxRecord[] {
  return [...boxes].sort((a, b) => {
    const priority = (box: FotoboxRecord): number => {
      if (box.status === 'offline') return 3;
      if (box.printer_status === 'error') return 0;
      if (box.printer_status === 'warning') return 1;
      if (box.prints_remaining !== null && box.prints_remaining <= 20) return 2;
      return 4;
    };
    const diff = priority(a) - priority(b);
    if (diff !== 0) return diff;
    return a.name.localeCompare(b.name, 'de');
  });
}

export function useBoxes(token: string) {
  const [boxMap, setBoxMap] = useState<Map<string, FotoboxRecord>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  const fetchBoxes = useCallback(() => {
    fetch(`${API_URL}/api/boxes`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (r.status === 401) throw new Error('401');
        if (!r.ok) throw new Error('Server-Fehler');
        return r.json() as Promise<FotoboxRecord[]>;
      })
      .then((data) => {
        setBoxMap(new Map(data.map((b) => [b.id, b])));
        setLastUpdated(new Date());
        setLoading(false);
        setError(null);
      })
      .catch((e: Error) => {
        setError(e.message);
        setLoading(false);
      });
  }, [token]);

  // Initialer Load
  useEffect(() => {
    fetchBoxes();
  }, [fetchBoxes]);

  // Safety-Net 1: Sofort neu laden wenn Tab wieder sichtbar wird
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchBoxes();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [fetchBoxes]);

  // Safety-Net 2: Periodisches Polling alle 60s (fängt verlorene Socket-Events auf)
  useEffect(() => {
    const interval = setInterval(fetchBoxes, 60_000);
    return () => clearInterval(interval);
  }, [fetchBoxes]);

  const updateBox = useCallback((box: FotoboxRecord) => {
    setBoxMap((prev) => new Map(prev).set(box.id, box));
    setLastUpdated(new Date());
  }, []);

  const boxes = sortBoxes(Array.from(boxMap.values()));

  return { boxes, loading, error, lastUpdated, updateBox, refetch: fetchBoxes };
}
